import { initializeApp, getApps } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { detectEnvironmentMismatch } from './hostEnvironment';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

if (!firebaseConfig.projectId) {
  console.error('Firebase config missing! Ensure VITE_FIREBASE_* env vars are set in .env.development or .env.production');
}

// Last-line-of-defense safety check: refuse to talk to the wrong Firebase
// project. Vite bakes env vars at build time, so a `npm run build` (which
// defaults to --mode production) followed by deploy to the dev hosting target
// would silently point admin-dev at production data. Catch that here at boot.
//
// The host classification lives in ./hostEnvironment so it can be unit-tested
// without importing this module (which initializes Firebase as a side effect).
// It used to be inline substring matching that missed BOTH default hosting
// URLs — see that file's header and its test suite.
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

function renderMismatchBanner(msg: string): void {
  if (typeof document === 'undefined') return;
  const wrap = document.createElement('div');
  wrap.style.cssText =
    'padding:32px;font-family:sans-serif;color:#a8071a;background:#fff1f0;' +
    'border:2px solid #a8071a;margin:32px;border-radius:8px;';
  const h = document.createElement('h1');
  h.style.marginTop = '0';
  h.textContent = 'Build/Environment Mismatch';
  const pre = document.createElement('pre');
  pre.style.cssText = 'white-space:pre-wrap;margin:0;';
  pre.textContent = msg;
  wrap.appendChild(h);
  wrap.appendChild(pre);
  document.body.replaceChildren(wrap);
}

const mismatch = detectEnvironmentMismatch(hostname, firebaseConfig.projectId);
if (mismatch) {
  console.error(mismatch);
  renderMismatchBanner(mismatch);
  throw new Error(mismatch);
}

// Initialize Firebase only if it hasn't been initialized yet
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');

// Connect to emulators in development
if (import.meta.env.DEV && window.location.hostname === 'localhost') {
  const useEmulators = localStorage.getItem('useEmulators') === 'true';

  if (useEmulators) {
    try {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      connectFirestoreEmulator(db, 'localhost', 8080);
      connectFunctionsEmulator(functions, 'localhost', 5001);
      console.log('Connected to Firebase emulators');
    } catch (error) {
      console.warn('Failed to connect to emulators:', error);
    }
  }
}

export default app;
